import {
  SCHEDULE_TIMEZONE_IDS,
  US_TIMEZONES,
  isSelectableTimezone,
  type UsTimezone,
} from "@shared/types/schedules";

/**
 * How a time zone reads on screen (FR-50, UX-5).
 *
 * `America/Los_Angeles` is a database key, not a sentence. Every place this product
 * shows or offers a zone shows the same three things instead: what a person calls it, the
 * abbreviation they would say out loud, and **what time it is there right now** — "Eastern (EDT)
 * · 9:42 AM". The current time is the part that earns its place: an operator in Phoenix
 * scheduling an import for a clinic in Michigan has no other way to check that "02:00 Eastern"
 * is the middle of the night rather than the middle of their afternoon.
 *
 * The abbreviation is asked of `Intl` at render time rather than stored, so it is PDT in August
 * and PST in January without anything here knowing the daylight saving rules. `US_TIMEZONES`
 * carries a generic form as the fallback for an engine whose ICU answers with a GMT offset.
 *
 * The locale is pinned to `en-US`: the product is sold in the United States, the times are
 * American clock times, and a fixed locale is also what makes the rendering testable.
 */

const LOCALE = "en-US";

/**
 * Labels for the US zones that are accepted and no longer offered (Session 10, B6).
 *
 * A row can still hold one of these, and when it does the screen names it the way a person
 * would — "Eastern (Michigan)" — and never as `America/Detroit`. `UTC` is not here: its name is
 * already what a person calls it.
 */
export const LEGACY_US_TIMEZONES: readonly UsTimezone[] = [
  { zone: "America/Detroit", label: "Eastern (Michigan)", abbreviation: "ET" },
  { zone: "America/Indiana/Indianapolis", label: "Eastern (Indiana)", abbreviation: "ET" },
  { zone: "America/Boise", label: "Mountain (Idaho)", abbreviation: "MT" },
];

/**
 * A browser zone the list does not carry, mapped to the offered zone with the same clock.
 *
 * A new schedule starts in the browser's zone; an operator in Detroit expects Eastern to be
 * selected, not the fallback, and one in Boise expects Mountain rather than Eastern.
 */
export const ZONE_ALIASES: Readonly<Record<string, string>> = {
  "America/Detroit": "America/New_York",
  "America/Indiana/Indianapolis": "America/New_York",
  "America/Boise": "America/Denver",
  // Review r6fix2 N5: some devices report UTC as `Etc/UTC`; it is the offered UTC.
  "Etc/UTC": "UTC",
};

/** The offered zone a browser zone stands for, or null when the list has no equivalent. */
export function offeredZoneFor(zone: string | null | undefined): string | null {
  if (!zone) return null;
  if (isSelectableTimezone(zone)) return zone;
  const alias = ZONE_ALIASES[zone];
  return alias && isSelectableTimezone(alias) ? alias : null;
}

const BY_ZONE = new Map<string, UsTimezone>(
  [...US_TIMEZONES, ...LEGACY_US_TIMEZONES].map((entry) => [entry.zone, entry]),
);

/** True for a US zone the product used to offer and still accepts (Michigan, Indiana, Idaho). */
export function isLegacyUsTimezone(zone: string): boolean {
  return LEGACY_US_TIMEZONES.some((entry) => entry.zone === zone);
}

/**
 * The list the schedule editor offers (the seven US zones, then UTC since the walk of 9a9d586,
 * D-e), plus `extra`, first, when a row already holds something else.
 */
export function offeredTimezones(extra?: string | null): string[] {
  const offered = [...SCHEDULE_TIMEZONE_IDS];
  if (extra && !isSelectableTimezone(extra)) return [extra, ...offered];
  return offered;
}

/** What a person calls the zone; an unknown zone is its own name. */
export function timezoneName(zone: string): string {
  return BY_ZONE.get(zone)?.label ?? zone;
}

/**
 * The abbreviation in force at `at` — EDT, EST, MST, HST.
 *
 * A runtime that answers `GMT-7` instead of a name falls back to the generic abbreviation for a
 * known zone, and keeps the offset for one it has never heard of: an offset is still true, and
 * inventing a name would not be.
 */
export function timezoneAbbreviation(zone: string, at: Date = new Date()): string {
  const fallback = BY_ZONE.get(zone)?.abbreviation ?? zone;
  try {
    const parts = new Intl.DateTimeFormat(LOCALE, {
      timeZone: zone,
      timeZoneName: "short",
    }).formatToParts(at);
    const name = parts.find((part) => part.type === "timeZoneName")?.value;
    if (!name) return fallback;
    return name.startsWith("GMT") && BY_ZONE.has(zone) ? fallback : name;
  } catch {
    return fallback;
  }
}

/** The wall clock in that zone right now: `9:42 AM`. */
export function timezoneCurrentTime(zone: string, at: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat(LOCALE, {
      timeZone: zone,
      hour: "numeric",
      minute: "2-digit",
    }).format(at);
  } catch {
    return "";
  }
}

/**
 * The zone without a clock: `Eastern (EDT)`.
 *
 * What a live region and a sentence use. The current time deliberately stays out of it: a polite
 * live region whose text changes every minute is re-announced every minute, which turns a helpful
 * sentence into a screen reader talking over the user once a minute.
 */
export function timezoneShortLabel(zone: string, at: Date = new Date()): string {
  const name = timezoneName(zone);
  const abbreviation = timezoneAbbreviation(zone, at);
  if (!abbreviation || abbreviation === name) return name;
  // "Eastern (Michigan)" already ends in a parenthesis; the abbreviation joins it rather than
  // opening a second one: "Eastern (Michigan, EDT)".
  if (name.endsWith(")")) return `${name.slice(0, -1)}, ${abbreviation})`;
  return `${name} (${abbreviation})`;
}

/**
 * The one label: `Eastern (EDT) · 9:42 AM`.
 *
 * The abbreviation is dropped when it would repeat the name (`UTC (UTC)`), and the time is
 * dropped when the runtime could not format the zone at all — a zone nothing can render is
 * still shown by name, because it is what the row holds and hiding it would make a stored
 * schedule look empty.
 */
export function timezoneLabel(zone: string, at: Date = new Date()): string {
  const head = timezoneShortLabel(zone, at);
  const time = timezoneCurrentTime(zone, at);
  return time ? `${head} · ${time}` : head;
}

/**
 * A schedule zone as its picker option reads it (round 10, C1): `Pacific (PDT) · now 9:25 AM`.
 *
 * Walk 2026-10-02 (D6) took the clock out of the picker because "Pacific (PDT) · 12:25 AM" read
 * like a run time beside the schedule's own times; the word "now" says what it is. Only the
 * options carry it: the closed field shows `timezoneShortLabel`, and no live region reads it.
 */
export function timezonePickerLabel(zone: string, now: Date = new Date()): string {
  const head = timezoneShortLabel(zone, now);
  const time = timezoneCurrentTime(zone, now);
  return time ? `${head} · now ${time}` : head;
}

/** Where no zone can be read: Pacific, where Skypoint is (owner, #1078). */
const PACIFIC_ZONE = "America/Los_Angeles";

/**
 * An instant as it reads in the schedule's own zone: `Tue Sep 30, 2:00 AM EDT`.
 *
 * The zone is the schedule's, not the browser's: "Next run 2:00 AM" beside a schedule set in
 * Eastern must be 2:00 AM Eastern whoever is looking at it, and the abbreviation says so.
 */
export function formatNextFire(at: Date, zone: string, abbreviation?: string): string {
  try {
    const parts = new Intl.DateTimeFormat(LOCALE, {
      timeZone: zone,
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).formatToParts(at);
    const part = (type: Intl.DateTimeFormatPartTypes): string =>
      parts.find((entry) => entry.type === type)?.value ?? "";
    const time = `${part("hour")}:${part("minute")} ${part("dayPeriod")}`.trim();
    return `${part("weekday")} ${part("month")} ${part("day")}, ${time} ${abbreviation ?? timezoneAbbreviation(zone, at)}`;
  } catch {
    // #1078: never a raw ISO string. A zone this engine cannot format reads in Pacific, named.
    return zone === PACIFIC_ZONE ? "—" : formatNextFire(at, PACIFIC_ZONE);
  }
}
